import moment from 'moment-timezone';

import { DateRangeValueType } from '@core/date-range-types';
import { DataInterval, ReportDateFilter, SortedIntervals } from '@core/reports-types';
import { resolveWeekType } from '@core/time-legacy';

import type { TimeSpan, TimeSpanValue } from './time-span';
import { timeSpan } from './time-span';

export const resolveDataInterval = (
  interval: DataInterval,
  dateFilter: ReportDateFilter,
  accountCreatedAt: moment.Moment,
) => {
  const available = SortedIntervals.filter(_interval =>
    isDataIntervalAvailable(_interval, dateFilter, accountCreatedAt),
  );

  if (available.includes(interval)) {
    return interval;
  }

  return available.shift();
};

export const findStartDateOfPredefinedRange = (
  dateFilter: ReportDateFilter,
): moment.Moment | undefined => {
  const now = moment().startOf('day');
  const weekType = resolveWeekType();

  switch (dateFilter.dateRange) {
    case DateRangeValueType.Today:
      return now.startOf('day');
    case DateRangeValueType.Yesterday:
      return now.subtract(1, 'day');
    case DateRangeValueType.ThisWeek:
      return now.startOf(weekType);
    case DateRangeValueType.LastWeek:
      return now.subtract(1, 'week').startOf(weekType);
    case DateRangeValueType.ThisMonth:
      return now.startOf('month');
    case DateRangeValueType.LastMonth:
      return now.subtract(1, 'month').startOf('month');
    case DateRangeValueType.ThisQuarter:
      return now.startOf('quarter');
    case DateRangeValueType.LastQuarter:
      return now.subtract(1, 'quarter').startOf('quarter');
    case DateRangeValueType.ThisYear:
      return now.startOf('year');
    case DateRangeValueType.LastYear:
      return now.subtract(1, 'year').startOf('year');
    case DateRangeValueType.Last7Days:
      return now.subtract(7, 'days');
    case DateRangeValueType.Last30Days:
      return now.subtract(30, 'days');
    case DateRangeValueType.Last60Days:
      return now.subtract(60, 'days');
    case DateRangeValueType.Last90Days:
      return now.subtract(90, 'days');
    case DateRangeValueType.Last365Days:
      return now.subtract(365, 'days');
    case 'range':
      return moment(dateFilter.dateStart);

    default:
      return undefined;
  }
};

export const findEndDateOfPredefinedRange = (
  dateFilter: ReportDateFilter,
): moment.Moment | undefined => {
  const now = moment().startOf('day');
  const weekType = resolveWeekType();

  switch (dateFilter.dateRange) {
    case DateRangeValueType.Today:
      return now.endOf('day');
    case DateRangeValueType.Yesterday:
      return now.subtract(1, 'day');
    case DateRangeValueType.ThisWeek:
      return now.endOf(weekType);
    case DateRangeValueType.LastWeek:
      return now.subtract(1, 'week').endOf(weekType);
    case DateRangeValueType.ThisMonth:
      return now.endOf('month');
    case DateRangeValueType.LastMonth:
      return now.subtract(1, 'month').endOf('month');
    case DateRangeValueType.ThisQuarter:
      return now.endOf('quarter');
    case DateRangeValueType.LastQuarter:
      return now.subtract(1, 'quarter').endOf('quarter');
    case DateRangeValueType.ThisYear:
      return now.endOf('year');
    case DateRangeValueType.LastYear:
      return now.subtract(1, 'year').endOf('year');
    case DateRangeValueType.Last7Days:
      return now;
    case DateRangeValueType.Last30Days:
      return now;
    case DateRangeValueType.Last60Days:
      return now;
    case DateRangeValueType.Last90Days:
      return now;
    case DateRangeValueType.Last365Days:
      return now;
    case 'range':
      return moment(dateFilter.dateEnd);

    default:
      return undefined;
  }
};

const intervalAvailabilityRules: Array<{
  interval: DataInterval;
  max?: Partial<Record<keyof TimeSpan, number>>;
  min?: Partial<Record<keyof TimeSpan, number>>;
}> = [
  { interval: DataInterval.Daily, min: { days: 0 }, max: { days: 48 } },
  { interval: DataInterval.Weekly, min: { weeks: 1 }, max: { weeks: 12 } },
  { interval: DataInterval.Monthly, min: { months: 1 }, max: { months: 24 } },
  { interval: DataInterval.Quarterly, min: { quarters: 1 } },
];

const findIntervals = (ts: TimeSpanValue) =>
  intervalAvailabilityRules.reduce(
    (acc, rule) => {
      const minRangeCheck = rule.min
        ? Object.entries(rule.min).every(([k, v]: [keyof TimeSpanValue, number]) => v! <= ts[k])
        : true;

      const maxRangeCheck = rule.max
        ? Object.entries(rule.max).every(([k, v]: [keyof TimeSpanValue, number]) => v! >= ts[k])
        : true;

      const inRange = minRangeCheck && maxRangeCheck;

      if (inRange) {
        acc.push(rule.interval);
      }

      return acc;
    },
    <DataInterval[]>[],
  );

const makeTimeSpan = (partial: Partial<TimeSpanValue>): TimeSpanValue => ({
  days: 0,
  weeks: 0,
  months: 0,
  quarters: 0,
  years: 0,
  ...partial,
});

export const getAvailableDataIntervals = (
  dateFilter: ReportDateFilter,
  accountCreatedAt: moment.Moment,
): DataInterval[] => {
  switch (dateFilter.dateRange) {
    case DateRangeValueType.Today:
    case DateRangeValueType.Yesterday:
      return findIntervals(makeTimeSpan({ days: 1 }));

    case DateRangeValueType.Last7Days:
    case DateRangeValueType.LastWeek:
    case DateRangeValueType.ThisWeek:
      return findIntervals(makeTimeSpan({ days: 7, weeks: 1 }));

    case DateRangeValueType.Last14Days:
      return findIntervals(makeTimeSpan({ days: 14, weeks: 2 }));

    case DateRangeValueType.Last30Days:
    case DateRangeValueType.LastMonth:
    case DateRangeValueType.ThisMonth:
      return findIntervals(makeTimeSpan({ days: 30, months: 1, weeks: 4 }));

    case DateRangeValueType.Last60Days:
      return findIntervals(makeTimeSpan({ days: 60, months: 2, weeks: 8 }));

    case DateRangeValueType.LastQuarter:
    case DateRangeValueType.ThisQuarter:
    case DateRangeValueType.Last90Days:
      return findIntervals(makeTimeSpan({ days: 90, months: 4, weeks: 12, quarters: 1 }));

    case DateRangeValueType.ThisYear:
    case DateRangeValueType.LastYear:
    case DateRangeValueType.Last365Days:
      return findIntervals(makeTimeSpan({ days: 365, months: 12, weeks: 48, quarters: 4 }));

    case DateRangeValueType.AllTime:
      if (accountCreatedAt) {
        const today = moment();

        return findIntervals(
          makeTimeSpan({
            days: Math.max(1, today.diff(accountCreatedAt, 'days')),
            weeks: today.diff(accountCreatedAt, 'weeks'),
            months: today.diff(accountCreatedAt, 'months'),
            quarters: today.diff(accountCreatedAt, 'quarters'),
          }),
        );
      }

      return findIntervals(makeTimeSpan({ days: -1, weeks: -1, months: 1, quarters: 1 }));

    case DateRangeValueType.Custom:
      return getAvailableDataIntervals(
        {
          dateRange: 'range',
          dateStart: accountCreatedAt.toISOString(),
          dateEnd: moment().toISOString(),
        },
        accountCreatedAt,
      );

    case 'range':
      const ts = timeSpan(moment(dateFilter.dateStart), moment(dateFilter.dateEnd));
      return findIntervals(ts);

    default:
      throw new Error(`Unhandled date range ${dateFilter.dateRange}`);
  }
};

const isDataIntervalAvailable = (
  interval: DataInterval,
  dateFilter: ReportDateFilter,
  accountCreatedAt: moment.Moment,
): boolean => getAvailableDataIntervals(dateFilter, accountCreatedAt).includes(interval);
